/**
 * Авто-детект стерео-звонка: у записи с АТС оператор и клиент уже разведены
 * по каналам, и разбирать её по каналам точнее и примерно на треть быстрее,
 * чем делить участников по голосу.
 *
 * 🔴 Вопрос здесь ровно один: несут ли каналы РАЗНЫХ людей. Сравнение отсчётов
 * на него не отвечает. До 21.09.2026 признаком служило «каналы не совпадают
 * побитово», и на сжатом звуке он давал «звонок» всегда: моно, продублированное
 * в два канала и закодированное Opus, после декодирования расходится в 9,6 %
 * отсчётов (замер на 39 с речи, libopus 64 кбит/с). Любая запись встречи из
 * Телемоста — webm со стерео-дорожкой — попадала на разбор по каналам, а он
 * делит одну и ту же сведённую дорожку надвое и выдаёт двух «участников»,
 * говорящих хором. Жалоба владельца 21.09.2026.
 *
 * Теперь считается доля времени, когда говорит ОДНА сторона: у сведённой
 * записи она нулевая (обе дорожки всегда звучат вместе), у звонка — близка к
 * единице. Замеры на тех же образцах: сведёнка 0,000, сведёнка с панорамой
 * 0,000, звонок 1,000, звонок с переслушкой −10 дБ 0,997.
 *
 * `decode` приходит снаружи — это `AudioContext.decodeAudioData` в браузере.
 * В jsdom его нет вовсе, поэтому параметр обязателен по контракту, а не
 * «на всякий случай»: так модуль проверяется тестом без звуковых файлов.
 */

// Кадр, на котором меряется громкость каждого канала. 50 мс — короче слога,
// длиннее периода основного тона: паузы между репликами видно, а отдельные
// звуки не дробятся.
const FRAME_MS = 50;
// Кадры берём равномерно по ВСЕЙ записи, а не подряд с начала: звонок часто
// начинается приветствием одной стороны, а встреча — тишиной.
const MAX_FRAMES = 2000;
// Тише сотой доли от самого громкого кадра — тишина; такие кадры не голосуют.
const SILENCE_SHARE = 0.02;
// Тихая сторона тише громкой более чем втрое — считаем, что она молчит.
// Порог держит и переслушку: микрофон соседа в канале даёт около 0,3.
const ONE_SIDED_RATIO = 0.35;
// Столько времени должна говорить одна сторона, чтобы это был звонок.
// Между измеренными 0,00 у сведёнки и 1,00 у звонка — с большим запасом.
const MIN_ONE_SIDED_SHARE = 0.15;
// Меньше пяти секунд звучащей речи — не судим: на обрывке одна сторона молчит
// просто потому, что до неё не дошла очередь.
const MIN_ACTIVE_FRAMES = 100;
const DEFAULT_SAMPLE_RATE = 48000;
// Память декодированного звука растёт с ДЛИТЕЛЬНОСТЬЮ, а не с размером файла:
// webm с Opus на 60 МБ — это 2–4 часа, стерео WAV с АТС на 100 МБ — минут сорок.
// Решаем по длительности; размер — запасной признак, когда браузер её не знает
// (webm из MediaRecorder отдаёт Infinity), и потолок на чтение файла целиком —
// он сильнее длительности (16-кГц WAV на полтора часа уже за ним).
// Пик памяти — это декодирование в ИСХОДНОЙ частоте: браузер сначала разворачивает
// файл целиком и лишь потом ресемплирует в частоту контекста (в Chrome на время
// ресемплинга — две копии).
//
// 🔴 Потолок — по оценке ПАМЯТИ, а не по минутам. До 01.11.2026 стоял порог
// «45 минут» для любого формата, и запись Zoom на 33 минуты роняла вкладку
// (обращение f4c1c88e; 10 минут у того же человека проходили). Сжатый файл
// (m4a, mp3, webm) не говорит частоту без разбора, поэтому считаем худшее —
// 48 кГц стерео с копией: 768 КБ на секунду, 10 минут ≈ 460 МБ, 33 ≈ 1,5 ГБ.
// У WAV память видна по размеру: PCM16 во float32 вдвое, плюс копия и сам
// прочитанный файл — около пяти размеров. Звонок АТС в WAV на 8 кГц разбирается
// и на час с лишним.
const DECODE_BUDGET_BYTES = 600 * 1024 * 1024;
const WORST_DECODED_BYTES_PER_SECOND = 48000 * 2 * 4 * 2;
const WAV_DECODE_FACTOR = 5;
// Длительность неизвестна (webm из MediaRecorder — это Opus, 3 КБ на секунду):
// разбираем только совсем короткие файлы.
const MAX_ANALYZE_BYTES_UNKNOWN_DURATION = 2 * 1024 * 1024;
const MAX_READ_BYTES = 300 * 1024 * 1024;

function isWav(file) {
  return /\.wav$/i.test(file.name || '') || /wav/i.test(file.type || '');
}

function tooBigToDecode(file, duration) {
  if (file.size > MAX_READ_BYTES) return true;
  if (isWav(file) && file.size > 0) return file.size * WAV_DECODE_FACTOR > DECODE_BUDGET_BYTES;
  if (Number.isFinite(duration) && duration > 0) {
    return duration * WORST_DECODED_BYTES_PER_SECOND > DECODE_BUDGET_BYTES;
  }
  return file.size > MAX_ANALYZE_BYTES_UNKNOWN_DURATION;
}

/**
 * Доля времени, когда слышна только одна сторона (0…1).
 *
 * Экспортируется ради тестов и разбора жалоб: по этому числу видно, почему
 * файл отнесён к звонку или к встрече.
 */
export function oneSidedShare(left, right, sampleRate = DEFAULT_SAMPLE_RATE) {
  const n = Math.min(left.length, right.length);
  const frame = Math.max(1, Math.round((sampleRate * FRAME_MS) / 1000));
  const total = Math.floor(n / frame);
  if (total < 1) return 0;

  const step = Math.max(1, Math.ceil(total / MAX_FRAMES));
  const loud = [];
  const quiet = [];
  for (let f = 0; f < total; f += step) {
    const from = f * frame;
    let sl = 0;
    let sr = 0;
    for (let i = from; i < from + frame; i += 1) {
      sl += left[i] * left[i];
      sr += right[i] * right[i];
    }
    const rl = Math.sqrt(sl / frame);
    const rr = Math.sqrt(sr / frame);
    loud.push(Math.max(rl, rr));
    quiet.push(Math.min(rl, rr));
  }

  const peak = loud.reduce((a, b) => (b > a ? b : a), 0);
  const floor = Math.max(peak * SILENCE_SHARE, 1e-4);
  let active = 0;
  let oneSided = 0;
  for (let i = 0; i < loud.length; i += 1) {
    if (loud[i] <= floor) continue;
    active += 1;
    if (quiet[i] / loud[i] < ONE_SIDED_RATIO) oneSided += 1;
  }
  if (active < MIN_ACTIVE_FRAMES) return 0;
  return oneSided / active;
}

/** Каналы файла: сколько их и как часто слышна только одна сторона. */
export async function analyzeChannels(file, { decode, duration = null }) {
  // 🔴 decodeAudioData разворачивает ВСЮ запись в несжатый звук: четыре часа
  // стерео на 48 кГц — около 5 ГБ, и вкладка падает с «Out of Memory» (обращение
  // 1921945b, 16.10.2026, файл 244 МБ). try/catch такое не ловит: падает сама
  // страница. Длинный файл не разбираем — «не знаем», обычный путь примет его.
  if (tooBigToDecode(file, duration)) return { channels: 0, oneSided: 0 };
  try {
    const audio = await decode(await file.arrayBuffer());
    if (audio.numberOfChannels < 2) {
      return { channels: audio.numberOfChannels, oneSided: 0 };
    }
    return {
      channels: 2,
      oneSided: oneSidedShare(
        audio.getChannelData(0), audio.getChannelData(1),
        audio.sampleRate || DEFAULT_SAMPLE_RATE,
      ),
    };
  } catch {
    // Не смогли разобрать — не повод отказывать в работе: обычный путь примет
    // и такой файл, звук из него извлечёт бэкенд. channels: 0 честно говорит
    // «не знаем», и разделение по каналам не предлагается.
    return { channels: 0, oneSided: 0 };
  }
}

/**
 * Разделять по каналам стоит только там, где стороны вправду разведены.
 *
 * 🔴 Сведённая запись встречи — это моно в стерео-контейнере, сколько бы
 * каналы ни отличались после сжатия. Разделение дало бы двух «участников»,
 * проговаривающих один и тот же текст.
 */
export function shouldSplitByChannels(analysis) {
  return analysis.channels === 2 && (analysis.oneSided || 0) >= MIN_ONE_SIDED_SHARE;
}
